import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from '../keys';
import type { Envelope, GameDetail, GameHistoryRow, GameType, Paginated } from '../types';

export interface GamesQuery {
  type?: GameType;
  page?: number;
  /** The legacy `/game?all=1` list: every status, not just ended games. */
  all?: boolean;
}

/** `GET /api/games` - paginated game history, optional CB/CV filter. */
export function useGames(params: GamesQuery = {}): UseQueryResult<Paginated<GameHistoryRow>> {
  const page = params.page ?? 1;
  const all = params.all === true;
  return useQuery({
    queryKey: queryKeys.games.list({ type: params.type, page, all }),
    // Keep the previous page visible while the next one loads (no flicker).
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get<Paginated<GameHistoryRow>>('/games', {
        query: { type: params.type, page, all: all ? 1 : undefined },
      }),
  });
}

/** `GET /api/games/{id}` - full game detail (live companies, log). */
export function useGame(id: number | undefined): UseQueryResult<GameDetail> {
  return useQuery({
    queryKey: queryKeys.games.detail(id ?? 0),
    enabled: id !== undefined,
    queryFn: async () => {
      const res = await api.get<Envelope<GameDetail>>(`/games/${encodeURIComponent(String(id))}`);
      return res.data;
    },
  });
}
